import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
  AppBar, Box, Button, Container, IconButton,
  Menu, MenuItem, Toolbar, Tooltip, Typography,
  Avatar, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { ReactComponent as LogoSvg } from '../logo.svg';
import { auth } from "../config/firebase";
import { useOrgContext } from './OrgContext';

const pages = ['Matches', 'Join'];
const settings = ['Profile'];

const PrimaryNavBar = () => {
  const { orgs, currentOrgId, setCurrentOrgId, getCurrentOrg } = useOrgContext();
  const org = getCurrentOrg();

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {org?.logoUrl ? (
            <Box
              component="img"
              sx={{ height: 48 }}
              alt="Logo"
              src={org.logoUrl}
            />
          ) : (
            <MenuIcon component={LogoSvg} viewBox="0 0 1182 1044" fontSize="large" />
          )}

          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2, ml: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {org?.name || "Speedcubing.tv"}
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {pages.map((page) => (
                <MenuItem key={page} onClick={() => {
                  handleCloseNavMenu();
                  navigate(`/${page.toLowerCase() === 'matches' ? "" : page.toLowerCase()}`);
                }}>
                  <Typography>{page}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Typography
            variant="h5"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            LOGO
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button
                key={page}
                onClick={() => {
                  handleCloseNavMenu();
                  navigate(`/${page.toLowerCase() === 'matches' ? "" : page.toLowerCase()}`);
                }}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                {page}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  alt={auth.currentUser?.displayName || ""}
                  src={auth.currentUser?.photoURL || ""}
                />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {settings.map((setting) => (
                <MenuItem key={setting} onClick={() => {
                  handleCloseUserMenu();
                  navigate(`/${setting.toLowerCase()}`);
                }}>
                  <Typography textAlign="center">{setting}</Typography>
                </MenuItem>
              ))}

              <Divider />
              <MenuItem disabled>
                <Typography variant="caption">Switch Organization</Typography>
              </MenuItem>

              {orgs.map((o) => (
                <MenuItem
                  key={o.id}
                  selected={o.id === currentOrgId}
                  onClick={() => {
                    handleCloseUserMenu();
                    if (o.id !== currentOrgId) {
                      setCurrentOrgId(o.id);
                      localStorage.setItem("currentOrgId", o.id); // persist selection
                    }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {o.logoUrl ? (
                      <Box
                        component="img"
                        src={o.logoUrl}
                        alt="org logo"
                        sx={{ width: 20, height: 20, borderRadius: 1 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 20, height: 20 }}>{o.name[0]}</Avatar>
                    )}
                    <Typography variant="body2" noWrap>
                      {o.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}

              <Divider />
              <MenuItem onClick={() => {
                handleCloseUserMenu();
                signOut(auth)
                  .then(() => navigate("/login"))
                  .catch((error) => console.error(error));
              }}>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default PrimaryNavBar;
